import { AuthContext } from '../types/auth';

/**
 * The user's resolved authentication situation on THIS device. Each value maps
 * to exactly one message + one action on the auth page (see AuthPage / the
 * state→message→action table in the smart-login design).
 */
export enum AuthState {
  /** Email is not recognized by the system at all. */
  Unknown = 'Unknown',
  /** Known user AND a passkey exists for this device (rpId) — proceed to biometric. */
  ReadyToSignIn = 'ReadyToSignIn',
  /** Known user, but no passkey on this device — offer to add this device. */
  UserKnownDeviceUnknown = 'UserKnownDeviceUnknown',
  /** A passkey exists here but the browser prompt was cancelled or timed out — offer retry. */
  PasskeyDismissed = 'PasskeyDismissed',
  /**
   * The sign-in ceremony was dismissed/timed out on a device we BELIEVED held a
   * credential (ctx.hasCredentialForThisDevice was true) — offer BOTH retry and
   * add-device (the escape hatch). The browser may hold no usable passkey even
   * though the hostname has one recorded in the DB under the same rpId, so a
   * retry-only state would loop forever. Distinct from PasskeyDismissed so
   * viewForState stays a pure switch on the enum.
   */
  PasskeyDismissedCanAddDevice = 'PasskeyDismissedCanAddDevice',
  /** A passkey was expected but authentication was rejected (revoked/stale) — offer to re-add this device. */
  PasskeyNoMatch = 'PasskeyNoMatch',
  /** New-account attempt with no approved invitation — offer to request an invite. */
  NewUserNeedsInvite = 'NewUserNeedsInvite',
  /** Add-device attempt with no approved device request for this IP — offer to request access. */
  DeviceNotApproved = 'DeviceNotApproved',
  /** A device-access request already exists and is awaiting approval — tell the user to wait / retry. */
  DeviceRequestPending = 'DeviceRequestPending',
  /** A device request was approved but the approval window lapsed — loop back to request access. */
  ApprovalExpired = 'ApprovalExpired',
}

/**
 * Normalized classification of a WebAuthn ceremony failure, derived from the
 * browser's DOMException name. Passed to resolveAuthState so a failed attempt
 * refines the state (e.g. dismissed vs genuine no-match).
 */
export type AuthErrorKind =
  | 'dismissed' // NotAllowedError / AbortError — cancelled or timed out (ambiguous)
  | 'no-match' // credential expected but rejected/absent
  | 'other' // SecurityError, InvalidStateError, unknown
  | null;

/**
 * Maps a caught WebAuthn error to an AuthErrorKind. The browser overloads
 * NotAllowedError for cancel, timeout, AND no-credential, so we treat it as
 * "dismissed" (the common case) and let context decide whether a credential
 * was even expected.
 */
export function classifyAuthError(
  errorName: string | undefined,
): AuthErrorKind {
  switch (errorName) {
    case 'NotAllowedError':
    case 'AbortError':
      return 'dismissed';
    case 'InvalidStateError':
      return 'no-match';
    case 'SecurityError':
      return 'other';
    case undefined:
    case null:
      return null;
    default:
      return 'other';
  }
}

interface ResolveOptions {
  /** True when the user explicitly chose the "set up a new account" path. */
  intendsNewAccount?: boolean;
  /** True when the user explicitly chose the "add this device" path. */
  intendsAddDevice?: boolean;
  /** Result of a just-attempted WebAuthn ceremony, if any. */
  lastError?: AuthErrorKind;
}

/**
 * Pure router: given the auth context (and optional user intent / last ceremony
 * error), return the single AuthState that drives the UI. No side effects.
 *
 * Order of precedence:
 *  1. Explicit "add device" intent → device-approval gating.
 *  2. Explicit "new account" intent → invite gating.
 *  3. A just-failed sign-in ceremony → dismissed vs no-match.
 *  4. Plain context routing (unknown / ready / device-unknown).
 */
export function resolveAuthState(
  ctx: AuthContext,
  options: ResolveOptions = {},
): AuthState {
  const { intendsNewAccount, intendsAddDevice, lastError } = options;

  // 1. User explicitly wants to add/register THIS device.
  if (intendsAddDevice) {
    if (ctx.deviceRequest?.active) {
      // Approved + unexpired → they can proceed to register the device.
      return AuthState.ReadyToSignIn;
    }
    if (ctx.deviceRequest?.status === 'approved') {
      // Reached only when active === false: approved but the window lapsed.
      return AuthState.ApprovalExpired;
    }
    if (ctx.deviceRequest && ctx.deviceRequest.status === 'pending') {
      return AuthState.DeviceRequestPending;
    }
    return AuthState.DeviceNotApproved;
  }

  // 2. User explicitly wants to set up a brand-new account.
  if (intendsNewAccount) {
    if (ctx.invite?.active) {
      return AuthState.ReadyToSignIn; // has an approved invite → can register
    }
    return AuthState.NewUserNeedsInvite;
  }

  // 3. A sign-in ceremony was just attempted and failed.
  if (lastError) {
    if (lastError === 'dismissed') {
      // If we believed a credential existed here but the ceremony was
      // dismissed/timed out, the browser may simply hold no usable passkey —
      // offer the add-device escape hatch alongside retry so the user isn't
      // stuck in a retry loop. Otherwise it's a plain cancel with no credential
      // expected here: retry-only (don't mislabel a genuine cancel).
      return ctx.hasCredentialForThisDevice
        ? AuthState.PasskeyDismissedCanAddDevice
        : AuthState.PasskeyDismissed;
    }
    // no-match / other: if we believed a credential existed here, it's a
    // genuine no-match; otherwise fall through to context routing.
    if (ctx.hasCredentialForThisDevice) {
      return AuthState.PasskeyNoMatch;
    }
  }

  // 4. Plain context routing (initial press of Sign in).
  if (!ctx.userExists) {
    return AuthState.Unknown;
  }
  if (ctx.hasCredentialForThisDevice) {
    return AuthState.ReadyToSignIn;
  }
  return AuthState.UserKnownDeviceUnknown;
}
